import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth, db, getEnvDbPrefix } from '@/core/firebase/config';
import type { User as AppUser } from '@/features/auth/types';
import { onValue, ref } from 'firebase/database';

interface AuthContextType {
  user: (AppUser & FirebaseUser) | null; // Mix of app user and firebase user
  loading: boolean;
  initialized: boolean;
  /** False until the signed-in user's role has been read. The first user object uses a placeholder role. */
  roleReady: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  initialized: false,
  roleReady: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<(AppUser & FirebaseUser) | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [roleReady, setRoleReady] = useState(false);

  useEffect(() => {
    let unsubscribeDb: (() => void) | null = null;

    let roleUid: string | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async firebaseUser => {
      // Clean up previous DB subscription if any
      if (unsubscribeDb) {
        unsubscribeDb();
        unsubscribeDb = null;
      }

      if (firebaseUser) {
        // A new sign-in still has the placeholder role until the profile snapshot arrives.
        if (roleUid !== firebaseUser.uid) {
          roleUid = firebaseUser.uid;
          setRoleReady(false);
        }

        // Immediately set a basic user from Firebase Auth data
        // This allows the app to render quickly while DB data loads
        const basicUser = {
          ...firebaseUser,
          id: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          image: firebaseUser.photoURL || '',
          role: 'user' as const,
          treatsPreference: [] as string[],
          dietaryRestrictions: [] as string[],
        } as AppUser & FirebaseUser;

        setUser(basicUser);
        setLoading(false);
        setInitialized(true);

        // Subscribe to user data updates from Realtime Database
        // This will enrich the user object with additional data
        const userRef = ref(db, getEnvDbPrefix(`user/${firebaseUser.uid}`));

        unsubscribeDb = onValue(
          userRef,
          snapshot => {
            const userData = snapshot.val();

            // Treats preference logic
            let treatsPreference: string[] = [];
            if (userData?.treatsPreference) {
              treatsPreference = userData.treatsPreference;
            }

            // Dietary restrictions logic
            let dietaryRestrictions: string[] = [];
            if (userData?.dietaryRestrictions) {
              dietaryRestrictions = userData.dietaryRestrictions;
            }

            // Merge Firebase auth user with DB user data
            const mergedUser = {
              ...firebaseUser,
              id: firebaseUser.uid,
              name: userData?.userDisplayName || firebaseUser.displayName || '',
              email: userData?.userEmail || firebaseUser.email || '',
              image: userData?.userImage || firebaseUser.photoURL || '',
              role: userData?.role || (userData?.isAdmin ? 'admin' : 'user'),
              treatsPreference: treatsPreference,
              dietaryRestrictions: dietaryRestrictions,
              isRemoteWorker: userData?.isRemoteWorker || false,
              notificationPreferences: userData?.notifications?.preferences || {},
            } as AppUser & FirebaseUser;

            setUser(mergedUser);
            setRoleReady(true);
          },
          () => {
            // The profile read failed. Keep the placeholder role and stop waiting,
            // so admin routes can send the user away instead of hanging.
            setRoleReady(true);
          }
        );
      } else {
        roleUid = null;
        setUser(null);
        setLoading(false);
        setInitialized(true);
        setRoleReady(true);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeDb) {
        unsubscribeDb();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initialized, roleReady }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Supplies a demo user to preview components when no one is signed in (such as on the
 * public landing page), while keeping the real signed-in user whenever one exists.
 */
export function DemoAuthProvider({
  user: demoUser,
  children,
}: {
  user: Pick<AppUser, 'id' | 'name' | 'email' | 'image'>;
  children: React.ReactNode;
}) {
  const authContext = useAuth();
  const value = useMemo<AuthContextType>(
    () =>
      authContext.user
        ? authContext
        : {
            ...authContext,
            user: {
              ...demoUser,
              uid: demoUser.id,
              role: 'user',
              treatsPreference: [],
              dietaryRestrictions: [],
            } as unknown as AppUser & FirebaseUser,
          },
    [authContext, demoUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
